// src/modules/ExcelData.jsx
// Standalone Excel data science pipeline — completely independent from CSV module
// Uses SheetJS for parsing, all analysis in pure JS

import React, { useState, useRef, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, ScatterChart, Scatter,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';

import {
  parseExcel, inferExcelTypes, calcStats, calcHistogram, calcValueCounts,
  calcCorrelation, calcCorrelationMatrix, buildPivot, groupBy,
  detectDateCols, buildTimeSeries, movingAverage, linearTrend, calcGrowthRates,
  detectAnomalies, profileColumns, getMissingMatrix, crossSheetSummary,
  generateSampleExcel, parseNum,
} from '../../utils/excelProcessing.js';
import { Panel, Metric, Tag, Spinner, InfoBox, Tabs, LogPanel, timestamp } from '../../components/ui.jsx';

import { _livePalette as P } from '../../context/paletteSync';

const EXCEL_TABS = [
  { id: 'upload',    label: 'UPLOAD',    icon: '⬆' },
  { id: 'workbook',  label: 'WORKBOOK',  icon: '⬢', requiresData: true },
  { id: 'profile',   label: 'PROFILE',   icon: '◈', requiresData: true },
  { id: 'stats',     label: 'STATS',     icon: '⬟', requiresData: true },
  { id: 'pivot',     label: 'PIVOT',     icon: '◫', requiresData: true },
  { id: 'groupby',   label: 'GROUP BY',  icon: '⬡', requiresData: true },
  { id: 'timeseries',label: 'TIME SERIES',icon:'◎', requiresData: true },
  { id: 'anomaly',   label: 'ANOMALIES', icon: '◐', requiresData: true },
  { id: 'correlate', label: 'CORRELATE', icon: '◉', requiresData: true },
  { id: 'visualize', label: 'VISUALIZE', icon: '⬣', requiresData: true },
  { id: 'missing',   label: 'MISSING',   icon: '◑', requiresData: true },
];

export default function ExcelData() {
  const [tab, setTab] = useState('upload');
  const [excelData, setExcelData] = useState(null);   // parsed workbook
  const [activeSheet, setActiveSheet] = useState(''); // currently selected sheet
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadMsg, setLoadMsg] = useState('');
  const [logs, setLogs] = useState([{ ts: '00:00:00', msg: 'Excel Lab ready. Drop an .xlsx / .xls / .ods file.', t: 'info' }]);

  // Per-sheet derived state
  const [colTypes, setColTypes] = useState([]);
  const [profile, setProfile] = useState([]);
  const [corrMatrix, setCorrMatrix] = useState({ matrix: [], cols: [] });
  const [crossSheet, setCrossSheet] = useState([]);

  // Stats tab
  const [statsCol, setStatsCol] = useState('');
  const [histBins, setHistBins] = useState(20);

  // Pivot tab
  const [pivotRow, setPivotRow] = useState('');
  const [pivotCol, setPivotCol] = useState('');
  const [pivotVal, setPivotVal] = useState('');
  const [pivotAgg, setPivotAgg] = useState('sum');
  const [pivotData, setPivotData] = useState(null);

  // Group-by tab
  const [gbGroup, setGbGroup] = useState('');
  const [gbVal, setGbVal] = useState('');
  const [gbAgg, setGbAgg] = useState('sum');

  // Time-series tab
  const [tsDate, setTsDate] = useState('');
  const [tsVal, setTsVal] = useState('');
  const [tsMA, setTsMA] = useState(3);
  const [tsView, setTsView] = useState('raw'); // raw | growth | decompose

  // Anomaly tab
  const [zThreshold, setZThreshold] = useState(3);

  // Viz tab
  const [vizType, setVizType] = useState('bar');
  const [vizX, setVizX] = useState('');
  const [vizY, setVizY] = useState('');
  const [vizColor, setVizColor] = useState('');

  const fileRef = useRef();
  const log = (msg, t='info') => setLogs(l => [...l.slice(-80), { ts: timestamp(), msg, t }]);

  // ── Current sheet data shorthand ──
  const sheet = excelData?.sheets?.[activeSheet];
  const rows  = sheet?.rows  || [];
  const headers = sheet?.headers || [];
  const nc = colTypes.filter(t => t.type === 'numeric' || t.type === 'integer' || t.type === 'float' || t.type === 'percentage' || t.type === 'currency').map(t => t.col);
  const cc = colTypes.filter(t => t.type === 'categorical' || t.type === 'boolean').map(t => t.col);
  const dateCols = detectDateCols(headers, rows);
  const nullTotal = colTypes.reduce((s,t) => s + (t.nullCount||0), 0);

  // ── Load workbook ──
  const loadWorkbook = useCallback(async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx','xls','ods','xlsm','xlsb','csv'].includes(ext)) {
      log('Unsupported format. Use .xlsx .xls .ods .xlsm', 'err'); return;
    }
    setLoading(true);
    setLoadMsg('Loading SheetJS...');
    try {
      setLoadMsg('Parsing workbook...');
      const data = await parseExcel(file);
      setExcelData(data);

      const firstSheet = data.sheetNames[0];
      setActiveSheet(firstSheet);
      const sh = data.sheets[firstSheet];

      setLoadMsg('Inferring column types...');
      const types = inferExcelTypes(sh.headers, sh.rows);
      setColTypes(types);

      setLoadMsg('Profiling columns...');
      const prof = profileColumns(sh.rows, types);
      setProfile(prof);

      setLoadMsg('Computing correlations...');
      const numCols = types.filter(t => ['numeric','integer','float','currency','percentage'].includes(t.type)).map(t => t.col);
      const cm = calcCorrelationMatrix(sh.rows, numCols);
      setCorrMatrix(cm);

      // Cross-sheet summary
      const cs = crossSheetSummary(data);
      setCrossSheet(cs);

      // Set defaults
      if (numCols[0]) { setStatsCol(numCols[0]); setVizY(numCols[0]); setPivotVal(numCols[0]); setGbVal(numCols[0]); setTsVal(numCols[0]); }
      if (types.filter(t=>t.type==='categorical').length > 0) {
        const cat0 = types.find(t=>t.type==='categorical')?.col || '';
        const cat1 = types.filter(t=>t.type==='categorical')[1]?.col || cat0;
        setPivotRow(cat0); setPivotCol(cat1); setGbGroup(cat0);
        setVizX(cat0);
      } else if (sh.headers[0]) {
        setVizX(sh.headers[0]); setPivotRow(sh.headers[0]);
      }
      if (dateCols.length > 0) setTsDate(dateCols[0]);

      log(`✓ ${data.totalSheets} sheet(s) — "${firstSheet}": ${sh.rows.length} rows × ${sh.headers.length} cols`, 'ok');
      log(`${numCols.length} numeric cols | ${types.filter(t=>t.type==='categorical').length} categorical | ${nullTotal} nulls`);
      if (sh.formulaCount) log(`${sh.formulaCount} formulas detected`);
      if (sh.merges?.length) log(`${sh.merges.length} merged cell regions`);

      setTab('workbook');
    } catch (err) {
      log('Parse error: ' + err.message, 'err');
      console.error(err);
    }
    setLoading(false);
    setLoadMsg('');
  }, []);

  // Switch active sheet
  const switchSheet = (name) => {
    if (!excelData) return;
    setActiveSheet(name);
    const sh = excelData.sheets[name];
    if (!sh || sh.empty) { setColTypes([]); setProfile([]); setCorrMatrix({matrix:[],cols:[]}); return; }
    const types = inferExcelTypes(sh.headers, sh.rows);
    setColTypes(types);
    setProfile(profileColumns(sh.rows, types));
    const numCols = types.filter(t=>['numeric','integer','float','currency','percentage'].includes(t.type)).map(t=>t.col);
    setCorrMatrix(calcCorrelationMatrix(sh.rows, numCols));
    if (numCols[0]) { setStatsCol(numCols[0]); setVizY(numCols[0]); setPivotVal(numCols[0]); setGbVal(numCols[0]); setTsVal(numCols[0]); }
    const cat = types.find(t=>t.type==='categorical')?.col || sh.headers[0] || '';
    setPivotRow(cat); setGbGroup(cat); setVizX(cat);
    log(`Switched to sheet "${name}": ${sh.rows.length}×${sh.headers.length}`, 'ok');
  };

  // Pivot compute
  const computePivot = () => {
    if (!rows.length || !pivotRow || !pivotCol || !pivotVal) return;
    setPivotData(buildPivot(rows, pivotRow, pivotCol, pivotVal, pivotAgg));
    log(`Pivot: ${pivotRow} × ${pivotCol} → ${pivotAgg}(${pivotVal})`, 'ok');
  };

  // ── Derived data ──
  const histData = statsCol ? calcHistogram(rows, statsCol, histBins) : [];
  const statsData = statsCol ? calcStats(rows, statsCol) : null;
  const gbData = gbGroup && gbVal ? groupBy(rows, gbGroup, gbVal, gbAgg) : [];
  const tsRaw = tsDate && tsVal ? buildTimeSeries(rows, tsDate, tsVal) : [];
  const tsWithMA = tsRaw.length > 0 ? movingAverage(linearTrend(tsRaw), tsMA) : [];
  const tsGrowth = calcGrowthRates(tsWithMA);
  const anomalyResult = nc.length > 0 ? detectAnomalies(rows, nc, zThreshold) : { anomalies: [], totalAnomalyRows: 0 };
  const missingMatrix = rows.length > 0 ? getMissingMatrix(rows, headers.slice(0, 12)) : [];

  // ── SELECT HELPER ──
  const Sel = ({value, onChange, options, label}) => (
    <div>
      {label && <div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'4px'}}>{label}</div>}
      <select value={value} onChange={e=>onChange(e.target.value)}
        style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'7px 8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'2px',outline:'none'}}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'14px'}}>
      <Tabs tabs={EXCEL_TABS} active={tab} onChange={setTab} dataset={excelData} />

      {/* Sheet switcher bar */}
      {excelData && excelData.sheetNames.length > 1 && (
        <div style={{display:'flex',gap:'4px',flexWrap:'wrap',padding:'8px 12px',background:'var(--raised)',border:'1px solid var(--b0)',borderRadius:'3px'}}>
          <span style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',alignSelf:'center',marginRight:'6px'}}>SHEET:</span>
          {excelData.sheetNames.map(name => (
            <button key={name} onClick={()=>switchSheet(name)}
              style={{padding:'5px 14px',border:`1px solid ${activeSheet===name?'var(--green)':'var(--b0)'}`,background:activeSheet===name?'var(--ggreen)':'transparent',color:activeSheet===name?'var(--green)':'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',transition:'all .12s'}}>
              {name} {excelData.sheets[name]?.empty ? '(empty)' : `(${excelData.sheets[name]?.rows?.length}r)`}
            </button>
          ))}
        </div>
      )}

      {/* ══ UPLOAD ══ */}
      {tab === 'upload' && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
          <div
            onDragOver={e=>{e.preventDefault();setIsDragging(true)}}
            onDragLeave={()=>setIsDragging(false)}
            onDrop={e=>{e.preventDefault();setIsDragging(false);loadWorkbook(e.dataTransfer.files[0])}}
            onClick={()=>fileRef.current?.click()}
            style={{border:`2px dashed ${isDragging?'var(--green)':'var(--b1)'}`,borderRadius:'4px',padding:'52px 24px',textAlign:'center',cursor:'pointer',background:isDragging?'var(--ggreen)':'var(--surface)',transition:'all .2s'}}>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.ods,.xlsm,.xlsb" style={{display:'none'}} onChange={e=>loadWorkbook(e.target.files[0])} />
            {loading
              ? <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:'12px'}}>
                  <Spinner size={26}/><div style={{color:'var(--green)',fontSize:'12px'}}>{loadMsg}</div>
                </div>
              : <>
                  <div style={{fontSize:'40px',opacity:.35,marginBottom:'14px'}}>⬢</div>
                  <div style={{fontFamily:'Syne',fontWeight:700,fontSize:'18px',marginBottom:'7px',color:isDragging?'var(--green)':'var(--t1)'}}>Drop Excel File Here</div>
                  <div style={{fontSize:'12px',color:'var(--t3)',marginBottom:'18px'}}>.xlsx · .xls · .ods · .xlsm</div>
                  <div style={{padding:'7px 18px',border:'1px solid var(--b1)',borderRadius:'2px',display:'inline-block',fontSize:'10px',color:'var(--t2)',letterSpacing:'.1em'}}>ALL PARSING IN-BROWSER · NO UPLOAD</div>
                </>
            }
          </div>

          <Panel title="SAMPLE WORKBOOKS" accent="var(--green)">
            {[
              ['Sales Dashboard','sales','Monthly sales by region, product & time'],
              ['Financial Model','finance','Income statement + balance sheet (4 years)'],
              ['HR Analytics','hr','300 employees — salary, tenure, ratings'],
              ['Survey Data','survey','200 responses — NPS + Likert scales + segments'],
            ].map(([name, type, desc]) => (
              <div key={type} onClick={async()=>{setLoading(true);setLoadMsg('Generating...');const f=await generateSampleExcel(type);setLoading(false);loadWorkbook(f)}}
                style={{display:'flex',alignItems:'center',gap:'12px',padding:'12px',border:'1px solid var(--b0)',borderRadius:'3px',marginBottom:'6px',cursor:'pointer',background:'var(--raised)',transition:'all .15s'}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--green)';e.currentTarget.style.background='var(--hover)'}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--b0)';e.currentTarget.style.background='var(--raised)'}}>
                <span style={{fontSize:'22px',color:'var(--green)'}}>⬢</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:'12px',fontWeight:600,color:'var(--t1)'}}>{name}</div>
                  <div style={{fontSize:'10px',color:'var(--t3)'}}>{desc}</div>
                </div>
                <span style={{color:'var(--green)',fontSize:'12px'}}>→</span>
              </div>
            ))}
            <div style={{marginTop:'10px'}}><LogPanel logs={logs} height={80}/></div>
          </Panel>
        </div>
      )}

      {/* ══ WORKBOOK OVERVIEW ══ */}
      {tab === 'workbook' && excelData && (
        <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>
          {/* File metrics */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:'10px'}}>
            <Metric label="Sheets" value={excelData.totalSheets} color="var(--green)"/>
            <Metric label="Active Rows" value={rows.length.toLocaleString()} color="var(--cyan)"/>
            <Metric label="Columns" value={headers.length} color="var(--violet)"/>
            <Metric label="Formulas" value={sheet?.formulaCount||0} color="var(--gold)" sub="in active sheet"/>
            <Metric label="Merged Cells" value={sheet?.merges?.length||0} color="var(--orange)"/>
          </div>

          {/* Cross-sheet table */}
          {crossSheet.length > 1 && (
            <Panel title="ALL SHEETS SUMMARY" badge={`${crossSheet.length} sheets`} accent="var(--green)">
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                  <thead><tr style={{background:'var(--deep)'}}>
                    {['Sheet','Rows','Cols','Numeric','Null%','Dupes','Formulas','Merges'].map(h=>(
                      <th key={h} style={{padding:'7px 12px',textAlign:'left',color:'var(--t3)',fontSize:'10px',borderBottom:'1px solid var(--b0)',whiteSpace:'nowrap'}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {crossSheet.map((s,i)=>(
                      <tr key={s.name} onClick={()=>{switchSheet(s.name);setTab('workbook')}}
                        style={{borderBottom:'1px solid var(--b0)',background:s.name===activeSheet?'var(--ggreen)':i%2===0?'transparent':'rgba(0,0,0,.08)',cursor:'pointer'}}>
                        <td style={{padding:'8px 12px',color:s.name===activeSheet?'var(--green)':'var(--t1)',fontWeight:600}}>{s.name}</td>
                        <td style={{padding:'8px 12px',color:'var(--t2)'}}>{s.rows?.toLocaleString()||0}</td>
                        <td style={{padding:'8px 12px',color:'var(--t2)'}}>{s.cols||0}</td>
                        <td style={{padding:'8px 12px',color:'var(--cyan)'}}>{s.numericCols||0}</td>
                        <td style={{padding:'8px 12px',color:parseFloat(s.nullPct||0)>10?'var(--red)':'var(--green)'}}>{s.nullPct||0}%</td>
                        <td style={{padding:'8px 12px',color:s.dupCount>0?'var(--gold)':'var(--t2)'}}>{s.dupCount||0}</td>
                        <td style={{padding:'8px 12px',color:'var(--violet)'}}>{s.formulaCount||0}</td>
                        <td style={{padding:'8px 12px',color:'var(--t3)'}}>{s.mergeCount||0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}

          {/* Column manifest */}
          <Panel title="COLUMN MANIFEST" badge={`${headers.length} columns — sheet: ${activeSheet}`}>
            <div style={{maxHeight:'300px',overflowY:'auto',overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px',minWidth:'700px'}}>
                <thead><tr style={{background:'var(--deep)'}}>
                  {['#','Column','Type','Sub-type','Non-Null','Null%','Unique','Sample'].map(h=>(
                    <th key={h} style={{padding:'7px 12px',textAlign:'left',color:'var(--t3)',fontSize:'10px',borderBottom:'1px solid var(--b0)',whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {colTypes.map((ct,i)=>{
                    const nn = rows.length - (ct.nullCount||0);
                    const np = ((ct.nullCount||0)/Math.max(1,rows.length)*100).toFixed(1);
                    const sample = [...new Set(rows.slice(0,15).map(r=>r[ct.col]).filter(Boolean))].slice(0,3).join(', ');
                    const typeColors = {numeric:'var(--cyan)',integer:'var(--cyan)',float:'var(--cyan)',currency:'var(--gold)',percentage:'var(--orange)',date:'var(--violet)',boolean:'var(--green)',categorical:'var(--gold)',empty:'var(--t3)'};
                    return (
                      <tr key={ct.col} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'transparent':'rgba(0,0,0,.08)'}}>
                        <td style={{padding:'7px 12px',color:'var(--t3)',fontSize:'10px'}}>{i+1}</td>
                        <td style={{padding:'7px 12px',color:'var(--t1)',fontWeight:500}}>{ct.col}</td>
                        <td style={{padding:'7px 12px'}}><Tag color={typeColors[ct.type]||'var(--t2)'}>{ct.type}</Tag></td>
                        <td style={{padding:'7px 12px',color:'var(--t3)',fontSize:'10px'}}>{ct.subType||'—'}</td>
                        <td style={{padding:'7px 12px',color:'var(--t2)'}}>{nn}</td>
                        <td style={{padding:'7px 12px',color:parseFloat(np)>10?'var(--red)':parseFloat(np)>0?'var(--gold)':'var(--green)'}}>{np}%</td>
                        <td style={{padding:'7px 12px',color:'var(--t2)'}}>{ct.uniqueCount}</td>
                        <td style={{padding:'7px 12px',color:'var(--t3)',fontSize:'10px'}}>{sample}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Panel>

          {/* Data preview */}
          <Panel title="DATA PREVIEW" badge="first 10 rows">
            <div style={{overflowX:'auto'}}>
              <table style={{borderCollapse:'collapse',fontSize:'11px',minWidth:'600px'}}>
                <thead><tr style={{background:'var(--deep)'}}>
                  {headers.map(h=><th key={h} style={{padding:'7px 12px',textAlign:'left',color:'var(--green)',fontSize:'10px',borderBottom:'1px solid var(--b0)',whiteSpace:'nowrap'}}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {rows.slice(0,10).map((row,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid var(--b0)'}}>
                      {headers.map(h=>(
                        <td key={h} style={{padding:'6px 12px',color:'var(--t2)',whiteSpace:'nowrap'}}>
                          {row[h]===''||row[h]==null?<span style={{color:'var(--red)',opacity:.6}}>∅</span>:row[h]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          {/* Formula detection */}
          {sheet?.formulas?.length > 0 && (
            <Panel title="DETECTED FORMULAS" badge={`${sheet.formulaCount} total`} accent="var(--violet)">
              <div style={{display:'flex',flexWrap:'wrap',gap:'6px'}}>
                {sheet.formulas.slice(0,20).map(f=>(
                  <div key={f.addr} style={{padding:'5px 10px',background:'var(--gviolet)',border:'1px solid var(--violet)33',borderRadius:'2px',fontSize:'10px'}}>
                    <span style={{color:'var(--violet)',fontWeight:600}}>{f.addr}</span>
                    <span style={{color:'var(--t3)',marginLeft:'6px'}}>{f.formula}</span>
                  </div>
                ))}
                {sheet.formulaCount > 20 && <span style={{color:'var(--t3)',fontSize:'10px',alignSelf:'center'}}>…+{sheet.formulaCount-20} more</span>}
              </div>
            </Panel>
          )}
        </div>
      )}

      {/* ══ PROFILE ══ */}
      {tab === 'profile' && profile.length > 0 && (
        <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
            <Metric label="Total Rows" value={rows.length.toLocaleString()} color="var(--cyan)"/>
            <Metric label="Total Nulls" value={nullTotal} color={nullTotal>0?'var(--gold)':'var(--green)'} sub={`${(nullTotal/(rows.length*headers.length||1)*100).toFixed(1)}% of all cells`}/>
            <Metric label="Numeric Cols" value={nc.length} color="var(--cyan)"/>
            <Metric label="Categorical Cols" value={cc.length} color="var(--gold)"/>
          </div>

          {/* Profile cards */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:'12px'}}>
            {profile.map((p,i) => (
              <div key={p.col} style={{padding:'14px',background:'var(--surface)',border:`1px solid ${p.quality?.score>=80?'var(--green)22':p.quality?.score>=60?'var(--gold)22':'var(--red)22'}`,borderRadius:'4px'}}>
                {/* Header */}
                <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'10px'}}>
                  <div style={{width:'8px',height:'8px',borderRadius:'50%',background:P[i%P.length]}}/>
                  <div style={{fontWeight:600,fontSize:'13px',color:'var(--t1)',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.col}</div>
                  <Tag color={p.type==='numeric'||p.type==='integer'||p.type==='float'?'var(--cyan)':p.type==='date'?'var(--violet)':p.type==='currency'?'var(--gold)':'var(--gold)'}>{p.subType||p.type}</Tag>
                </div>

                {/* Quality score bar */}
                <div style={{marginBottom:'10px'}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
                    <span style={{fontSize:'9px',color:'var(--t3)'}}>QUALITY</span>
                    <span style={{fontSize:'9px',color:p.quality?.score>=80?'var(--green)':p.quality?.score>=60?'var(--gold)':'var(--red)',fontWeight:700}}>{p.quality?.score}% — {p.quality?.label}</span>
                  </div>
                  <div style={{height:'4px',background:'var(--b0)',borderRadius:'2px'}}>
                    <div style={{height:'100%',width:`${p.quality?.score||0}%`,background:p.quality?.score>=80?'var(--green)':p.quality?.score>=60?'var(--gold)':'var(--red)',borderRadius:'2px',transition:'width .3s'}}/>
                  </div>
                  {p.quality?.issues?.length>0 && <div style={{fontSize:'9px',color:'var(--red)',marginTop:'3px'}}>{p.quality.issues.join(' · ')}</div>}
                </div>

                {/* Numeric stats */}
                {p.stats && (
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'5px'}}>
                    {[['Mean',p.stats.mean],['Std',p.stats.std],['Min',p.stats.min],['Median',p.stats.median],['Max',p.stats.max],['Skew',p.stats.skewness],['Q1',p.stats.q1],['Q3',p.stats.q3],['Outliers',`${p.stats.outlierCount} (${p.stats.outlierPct}%)`]].map(([k,v])=>(
                      <div key={k} style={{background:'var(--raised)',padding:'5px 7px',borderRadius:'2px'}}>
                        <div style={{fontSize:'8px',color:'var(--t3)'}}>{k}</div>
                        <div style={{fontSize:'11px',color:'var(--cyan)',fontWeight:600}}>{typeof v==='number'?v.toFixed?v.toFixed(3):v:v}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Categorical top vals */}
                {p.topVals && (
                  <div>
                    <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px'}}>TOP VALUES</div>
                    {p.topVals.slice(0,4).map(({value,count,pct})=>(
                      <div key={value} style={{display:'flex',alignItems:'center',gap:'6px',marginBottom:'3px'}}>
                        <span style={{fontSize:'10px',color:'var(--t1)',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{value}</span>
                        <div style={{width:'60px',height:'4px',background:'var(--b0)',borderRadius:'2px'}}>
                          <div style={{height:'100%',width:`${pct}%`,background:'var(--gold)',borderRadius:'2px'}}/>
                        </div>
                        <span style={{fontSize:'9px',color:'var(--gold)',width:'35px',textAlign:'right'}}>{pct}%</span>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{display:'flex',gap:'6px',marginTop:'8px'}}>
                  <Tag color="var(--t3)">nulls: {p.nullCount}</Tag>
                  <Tag color="var(--t3)">unique: {p.uniqueCount}</Tag>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══ STATS ══ */}
      {tab === 'stats' && (
        <div style={{display:'grid',gridTemplateColumns:'240px 1fr',gap:'14px'}}>
          {/* Column selector */}
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="SELECT COLUMN">
              <div style={{maxHeight:'280px',overflowY:'auto'}}>
                {colTypes.map(ct=>(
                  <div key={ct.col} onClick={()=>setStatsCol(ct.col)}
                    style={{padding:'8px 10px',cursor:'pointer',borderRadius:'2px',background:statsCol===ct.col?'var(--ggreen)':'transparent',border:`1px solid ${statsCol===ct.col?'var(--green)44':'transparent'}`,marginBottom:'2px',display:'flex',alignItems:'center',gap:'8px'}}>
                    <div style={{width:'6px',height:'6px',borderRadius:'50%',background:ct.type==='numeric'||ct.type==='integer'||ct.type==='float'?'var(--cyan)':ct.type==='date'?'var(--violet)':ct.type==='currency'?'var(--gold)':'var(--gold)',flexShrink:0}}/>
                    <span style={{fontSize:'11px',color:statsCol===ct.col?'var(--green)':'var(--t1)',fontWeight:statsCol===ct.col?600:400,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ct.col}</span>
                    <span style={{marginLeft:'auto',fontSize:'9px',color:'var(--t3)',flexShrink:0}}>{ct.subType||ct.type}</span>
                  </div>
                ))}
              </div>
            </Panel>
            <Panel title="HISTOGRAM BINS">
              <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'6px'}}>Bins: {histBins}</div>
              <input type="range" min={5} max={50} step={1} value={histBins} onChange={e=>setHistBins(parseInt(e.target.value))} style={{width:'100%',accentColor:'var(--green)'}}/>
            </Panel>
          </div>

          <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
            {/* Stats grid */}
            {statsData && (
              <>
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
                  <Metric label="Mean" value={statsData.mean} color="var(--cyan)"/>
                  <Metric label="Median" value={statsData.median} color="var(--green)"/>
                  <Metric label="Std Dev" value={statsData.std} color="var(--violet)"/>
                  <Metric label="CV%" value={statsData.cv+'%'} color="var(--gold)" sub="coeff of variation"/>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
                  <Metric label="Min" value={statsData.min} color="var(--t2)"/>
                  <Metric label="Max" value={statsData.max} color="var(--t2)"/>
                  <Metric label="Skewness" value={statsData.skewness} color={Math.abs(statsData.skewness)>1?'var(--orange)':'var(--green)'}/>
                  <Metric label="Kurtosis" value={statsData.kurtosis} color="var(--t2)"/>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
                  <Metric label="Sum" value={statsData.sum.toLocaleString()} color="var(--cyan)"/>
                  <Metric label="IQR" value={statsData.iqr} color="var(--violet)"/>
                  <Metric label="Outliers" value={`${statsData.outlierCount} (${statsData.outlierPct}%)`} color={statsData.outlierCount>0?'var(--red)':'var(--green)'}/>
                  <Metric label="Nulls" value={statsData.nullCount||0} color="var(--gold)"/>
                </div>
              </>
            )}

            {/* Percentile table */}
            {statsData && (
              <Panel title="PERCENTILE DISTRIBUTION" accent="var(--cyan)">
                <div style={{display:'grid',gridTemplateColumns:'repeat(8,1fr)',gap:'6px'}}>
                  {[['P5',statsData.p5],['P10',statsData.p10],['P25',statsData.q1],['P50',statsData.median],['P75',statsData.q3],['P90',statsData.p90],['P95',statsData.p95],['P99',statsData.p99]].map(([l,v])=>(
                    <div key={l} style={{background:'var(--raised)',padding:'8px',borderRadius:'2px',textAlign:'center'}}>
                      <div style={{fontSize:'9px',color:'var(--t3)'}}>{l}</div>
                      <div style={{fontSize:'12px',color:'var(--cyan)',fontWeight:700,fontFamily:'Syne'}}>{v}</div>
                    </div>
                  ))}
                </div>
              </Panel>
            )}

            {/* Histogram */}
            <Panel title="HISTOGRAM" badge={statsCol} accent="var(--green)">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={histData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="range" stroke="var(--t3)" tick={{fontSize:8}} interval="preserveStartEnd"/>
                  <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                  <Tooltip/>
                  <Bar dataKey="count" radius={[2,2,0,0]}>{histData.map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={0.85}/>)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </Panel>

            {/* Box plot */}
            {statsData && (() => {
              const range = statsData.max - statsData.min || 1;
              const p = v => ((v - statsData.min)/range)*100;
              return (
                <Panel title="BOX PLOT" accent="var(--cyan)">
                  <div style={{padding:'24px 20px 16px'}}>
                    <div style={{height:'44px',position:'relative'}}>
                      <div style={{position:'absolute',left:`${p(statsData.min)}%`,width:`${p(statsData.q1)-p(statsData.min)}%`,height:'2px',background:'var(--t3)',top:'50%',transform:'translateY(-50%)'}}/>
                      <div style={{position:'absolute',left:`${p(statsData.q3)}%`,width:`${p(statsData.max)-p(statsData.q3)}%`,height:'2px',background:'var(--t3)',top:'50%',transform:'translateY(-50%)'}}/>
                      <div style={{position:'absolute',left:`${p(statsData.min)}%`,width:'2px',height:'16px',background:'var(--t3)',top:'50%',transform:'translateY(-50%)'}}/>
                      <div style={{position:'absolute',left:`${p(statsData.max)}%`,width:'2px',height:'16px',background:'var(--t3)',top:'50%',transform:'translateY(-50%)'}}/>
                      <div style={{position:'absolute',left:`${p(statsData.q1)}%`,width:`${p(statsData.q3)-p(statsData.q1)}%`,height:'32px',top:'50%',transform:'translateY(-50%)',background:'var(--ggreen)',border:'1px solid var(--green)',borderRadius:'2px'}}/>
                      <div style={{position:'absolute',left:`${p(statsData.median)}%`,width:'2px',height:'32px',top:'50%',transform:'translateY(-50%)',background:'var(--red)',zIndex:1}}/>
                      <div style={{position:'absolute',left:`${p(statsData.mean)}%`,width:'10px',height:'10px',borderRadius:'50%',background:'var(--gold)',top:'50%',transform:'translate(-50%,-50%)',zIndex:2}}/>
                    </div>
                    <div style={{display:'flex',justifyContent:'space-between',fontSize:'10px',color:'var(--t3)',marginTop:'8px'}}>
                      <span>{statsData.min}</span>
                      <span style={{color:'var(--green)'}}>Q1:{statsData.q1}</span>
                      <span style={{color:'var(--red)'}}>Med:{statsData.median}</span>
                      <span style={{color:'var(--green)'}}>Q3:{statsData.q3}</span>
                      <span>{statsData.max}</span>
                    </div>
                  </div>
                  <div style={{display:'flex',gap:'14px',fontSize:'10px',padding:'0 4px 4px'}}>
                    <span style={{color:'var(--red)'}}>━ median</span>
                    <span style={{color:'var(--gold)'}}>● mean</span>
                    <span style={{color:'var(--green)'}}>□ IQR (Q1–Q3)</span>
                  </div>
                </Panel>
              );
            })()}
          </div>
        </div>
      )}

      {/* ══ PIVOT TABLE ══ */}
      {tab === 'pivot' && (
        <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>
          {/* Controls */}
          <Panel title="PIVOT CONFIGURATION" accent="var(--green)">
            <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr) auto',gap:'10px',alignItems:'end'}}>
              <Sel value={pivotRow} onChange={setPivotRow} options={headers} label="ROW DIMENSION"/>
              <Sel value={pivotCol} onChange={setPivotCol} options={cc.length?cc:headers} label="COLUMN DIMENSION"/>
              <Sel value={pivotVal} onChange={setPivotVal} options={nc.length?nc:headers} label="VALUE COLUMN"/>
              <Sel value={pivotAgg} onChange={setPivotAgg} options={['sum','mean','count','min','max','median','std']} label="AGGREGATION"/>
              <div/>
              <button onClick={computePivot}
                style={{padding:'10px 20px',background:'var(--ggreen)',border:'1px solid var(--green)',color:'var(--green)',fontSize:'11px',cursor:'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,letterSpacing:'.08em',whiteSpace:'nowrap'}}>
                BUILD →
              </button>
            </div>
          </Panel>

          {pivotData && (
            <Panel title="PIVOT TABLE" badge={`${pivotData.rowVals.length} rows × ${pivotData.colVals.length} cols`} accent="var(--green)">
              <div style={{overflowX:'auto',maxHeight:'500px',overflowY:'auto'}}>
                <table style={{borderCollapse:'collapse',fontSize:'11px',minWidth:'400px'}}>
                  <thead style={{position:'sticky',top:0}}>
                    <tr style={{background:'var(--deep)'}}>
                      <th style={{padding:'8px 14px',textAlign:'left',color:'var(--t3)',fontSize:'10px',borderBottom:'1px solid var(--b0)',borderRight:'1px solid var(--b0)',position:'sticky',left:0,background:'var(--deep)'}}>{pivotData.rowKey} \ {pivotData.colKey}</th>
                      {pivotData.colVals.map(cv=><th key={cv} style={{padding:'8px 14px',textAlign:'right',color:'var(--green)',fontSize:'10px',borderBottom:'1px solid var(--b0)',whiteSpace:'nowrap'}}>{cv}</th>)}
                      <th style={{padding:'8px 14px',textAlign:'right',color:'var(--cyan)',fontSize:'10px',borderBottom:'1px solid var(--b0)',borderLeft:'1px solid var(--b0)'}}>TOTAL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pivotData.data.map((row,i)=>{
                      const maxVal = Math.max(...pivotData.colVals.map(cv=>row[cv]||0));
                      return (
                        <tr key={row.__row} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'transparent':'rgba(0,0,0,.08)'}}>
                          <td style={{padding:'7px 14px',color:'var(--t1)',fontWeight:500,borderRight:'1px solid var(--b0)',whiteSpace:'nowrap',position:'sticky',left:0,background:i%2===0?'var(--surface)':'rgba(2,5,8,.95)'}}>{row.__row}</td>
                          {pivotData.colVals.map(cv=>{
                            const v = row[cv];
                            const pct = maxVal > 0 ? (v||0)/maxVal : 0;
                            return (
                              <td key={cv} style={{padding:'7px 14px',textAlign:'right',position:'relative'}}>
                                <div style={{position:'absolute',inset:0,background:`var(--cyan)`,opacity:pct*0.15,borderRadius:'2px'}}/>
                                <span style={{position:'relative',color:v===null?'var(--t3)':'var(--t1)'}}>{v===null?'—':v.toLocaleString()}</span>
                              </td>
                            );
                          })}
                          <td style={{padding:'7px 14px',textAlign:'right',color:'var(--cyan)',fontWeight:700,borderLeft:'1px solid var(--b0)'}}>{row.__total.toLocaleString()}</td>
                        </tr>
                      );
                    })}
                    {/* Totals row */}
                    <tr style={{background:'var(--deep)',borderTop:'2px solid var(--b1)'}}>
                      <td style={{padding:'8px 14px',color:'var(--cyan)',fontWeight:700,borderRight:'1px solid var(--b0)',position:'sticky',left:0,background:'var(--deep)'}}>TOTAL</td>
                      {pivotData.colVals.map(cv=><td key={cv} style={{padding:'8px 14px',textAlign:'right',color:'var(--green)',fontWeight:700}}>{pivotData.totals[cv]?.toLocaleString()||'—'}</td>)}
                      <td style={{padding:'8px 14px',textAlign:'right',color:'var(--green)',fontWeight:700,borderLeft:'1px solid var(--b0)'}}>{parseFloat(pivotData.totals.__total).toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Pivot heatmap chart */}
              {pivotData.data.length <= 15 && pivotData.colVals.length <= 6 && (
                <div style={{marginTop:'16px'}}>
                  <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>PIVOT CHART</div>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={pivotData.data.slice(0,15)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="__row" stroke="var(--t3)" tick={{fontSize:9}}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                      <Tooltip/>
                      <Legend/>
                      {pivotData.colVals.slice(0,6).map((cv,i)=>(
                        <Bar key={cv} dataKey={cv} fill={P[i%P.length]} opacity={0.85} radius={[2,2,0,0]}/>
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Panel>
          )}
          {!pivotData && <InfoBox color="var(--green)" icon="◈">Configure the pivot dimensions above and click BUILD to generate the table.</InfoBox>}
        </div>
      )}

      {/* ══ GROUP BY ══ */}
      {tab === 'groupby' && (
        <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>
          <Panel title="GROUP BY CONFIGURATION" accent="var(--violet)">
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'12px'}}>
              <Sel value={gbGroup} onChange={setGbGroup} options={headers} label="GROUP BY"/>
              <Sel value={gbVal} onChange={setGbVal} options={nc.length?nc:headers} label="VALUE COLUMN"/>
              <Sel value={gbAgg} onChange={setGbAgg} options={['sum','mean','count','min','max','std','median']} label="AGGREGATION"/>
            </div>
          </Panel>

          {gbData.length > 0 && (
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
              <Panel title="GROUP CHART" badge={`${gbData.length} groups`} accent="var(--violet)">
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={gbData.slice(0,20)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" horizontal={false}/>
                    <XAxis type="number" stroke="var(--t3)" tick={{fontSize:9}}/>
                    <YAxis type="category" dataKey="group" stroke="var(--t3)" tick={{fontSize:9}} width={100}/>
                    <Tooltip/>
                    <Bar dataKey="value" radius={[0,2,2,0]} label={{position:'right',fontSize:9,fill:'var(--t3)'}}>
                      {gbData.slice(0,20).map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={0.85}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Panel>

              <Panel title="GROUP TABLE">
                <div style={{maxHeight:'380px',overflowY:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                    <thead><tr style={{background:'var(--deep)'}}>
                      {['Group','Value','Count','Sum','Mean','Min','Max'].map(h=><th key={h} style={{padding:'6px 10px',textAlign:'left',color:'var(--t3)',fontSize:'10px',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {gbData.map((g,i)=>(
                        <tr key={g.group} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'transparent':'rgba(0,0,0,.08)'}}>
                          <td style={{padding:'6px 10px',color:'var(--t1)',fontWeight:500}}>{g.group}</td>
                          <td style={{padding:'6px 10px',color:P[i%P.length],fontWeight:700}}>{g.value.toLocaleString()}</td>
                          <td style={{padding:'6px 10px',color:'var(--t2)'}}>{g.count}</td>
                          <td style={{padding:'6px 10px',color:'var(--t2)'}}>{g.sum.toLocaleString()}</td>
                          <td style={{padding:'6px 10px',color:'var(--t2)'}}>{g.mean}</td>
                          <td style={{padding:'6px 10px',color:'var(--t2)'}}>{g.min}</td>
                          <td style={{padding:'6px 10px',color:'var(--t2)'}}>{g.max}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </div>
          )}
        </div>
      )}

      {/* ══ TIME SERIES ══ */}
      {tab === 'timeseries' && (
        <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>
          {/* Config */}
          <Panel title="TIME SERIES CONFIGURATION" accent="var(--cyan)">
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'12px',alignItems:'end'}}>
              <Sel value={tsDate} onChange={setTsDate} options={dateCols.length?dateCols:headers} label="DATE / TIME COLUMN"/>
              <Sel value={tsVal} onChange={setTsVal} options={nc.length?nc:headers} label="VALUE COLUMN"/>
              <div>
                <div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'5px'}}>MOVING AVG WINDOW: {tsMA}</div>
                <input type="range" min={2} max={12} value={tsMA} onChange={e=>setTsMA(parseInt(e.target.value))} style={{width:'100%',accentColor:'var(--cyan)'}}/>
              </div>
              <div style={{display:'flex',gap:'4px'}}>
                {[['raw','RAW'],['growth','GROWTH'],['decompose','TREND']].map(([v,l])=>(
                  <button key={v} onClick={()=>setTsView(v)} style={{flex:1,padding:'8px 4px',border:`1px solid ${tsView===v?'var(--cyan)':'var(--b0)'}`,background:tsView===v?'var(--gcyan)':'transparent',color:tsView===v?'var(--cyan)':'var(--t2)',fontSize:'9px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>{l}</button>
                ))}
              </div>
            </div>
          </Panel>

          {tsRaw.length === 0 && (
            <InfoBox color="var(--gold)" icon="⚠">
              {dateCols.length === 0
                ? 'No date column detected. Try a column with dates like "2024-01-15", "Q1 2024", or "Jan 2024".'
                : `Found date column "${dateCols[0]}". Select it above and pick a value column.`}
            </InfoBox>
          )}

          {tsRaw.length > 0 && tsView === 'raw' && (
            <>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
                <Metric label="Data Points" value={tsRaw.length} color="var(--cyan)"/>
                <Metric label="Min Value" value={Math.min(...tsRaw.map(d=>d.value)).toFixed(2)} color="var(--t2)"/>
                <Metric label="Max Value" value={Math.max(...tsRaw.map(d=>d.value)).toFixed(2)} color="var(--t2)"/>
                <Metric label="Latest Value" value={tsRaw[tsRaw.length-1]?.value.toFixed(2)||'—'} color="var(--green)"/>
              </div>
              <Panel title="TIME SERIES WITH MOVING AVERAGE & TREND" accent="var(--cyan)">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={tsWithMA}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="date" stroke="var(--t3)" tick={{fontSize:8}} interval="preserveStartEnd"/>
                    <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                    <Tooltip/>
                    <Legend/>
                    <Line type="monotone" dataKey="value" stroke="var(--cyan)" strokeWidth={1.5} dot={false} name="Actual"/>
                    <Line type="monotone" dataKey="ma" stroke="var(--gold)" strokeWidth={2} dot={false} strokeDasharray="5 3" name={`MA(${tsMA})`}/>
                    <Line type="monotone" dataKey="trend" stroke="var(--red)" strokeWidth={1.5} dot={false} strokeDasharray="8 4" name="Linear Trend"/>
                  </LineChart>
                </ResponsiveContainer>
              </Panel>
            </>
          )}

          {tsRaw.length > 0 && tsView === 'growth' && (
            <>
              <Panel title="PERIOD-OVER-PERIOD GROWTH RATE %" accent="var(--green)">
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={tsGrowth.filter(d=>d.growth!==null)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="date" stroke="var(--t3)" tick={{fontSize:8}} interval="preserveStartEnd"/>
                    <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                    <Tooltip formatter={v=>[`${v}%`,'Growth']}/>
                    <defs>
                      <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--green)" stopOpacity={0.3}/>
                        <stop offset="50%" stopColor="transparent" stopOpacity={0}/>
                        <stop offset="95%" stopColor="var(--red)" stopOpacity={0.3}/>
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="growth" stroke="var(--green)" fill="url(#growthGrad)" strokeWidth={2}/>
                  </AreaChart>
                </ResponsiveContainer>
              </Panel>
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px'}}>
                {(() => {
                  const growths = tsGrowth.filter(d=>d.growth!==null).map(d=>d.growth);
                  const avg = growths.reduce((s,v)=>s+v,0)/growths.length;
                  const pos = growths.filter(v=>v>0).length;
                  const neg = growths.filter(v=>v<0).length;
                  return [
                    <Metric key="avg" label="Avg Growth Rate" value={`${avg.toFixed(1)}%`} color={avg>=0?'var(--green)':'var(--red)'}/>,
                    <Metric key="pos" label="Positive Periods" value={pos} color="var(--green)" sub={`${(pos/growths.length*100).toFixed(0)}% of periods`}/>,
                    <Metric key="neg" label="Negative Periods" value={neg} color="var(--red)" sub={`${(neg/growths.length*100).toFixed(0)}% of periods`}/>,
                  ];
                })()}
              </div>
            </>
          )}

          {tsRaw.length > 0 && tsView === 'decompose' && (
            <Panel title="TREND DECOMPOSITION (Centered Moving Average)" accent="var(--violet)">
              <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'10px'}}>Showing: actual value vs linear trend line extracted via least-squares regression.</div>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={tsWithMA}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="date" stroke="var(--t3)" tick={{fontSize:8}} interval="preserveStartEnd"/>
                  <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                  <Tooltip/>
                  <Legend/>
                  <Line type="monotone" dataKey="value" stroke="var(--t3)" strokeWidth={1} dot={false} name="Actual" opacity={0.5}/>
                  <Line type="monotone" dataKey="ma" stroke="var(--cyan)" strokeWidth={2} dot={false} name={`Smooth MA(${tsMA})`}/>
                  <Line type="monotone" dataKey="trend" stroke="var(--red)" strokeWidth={2} dot={false} strokeDasharray="6 3" name="Trend"/>
                </LineChart>
              </ResponsiveContainer>
              {tsWithMA[0]?.slope !== undefined && (
                <InfoBox color="var(--cyan)" icon="◈">
                  Trend slope: <strong>{tsWithMA[0].slope}</strong> per period. {tsWithMA[0].slope > 0 ? '↑ Upward trend' : '↓ Downward trend'}.
                </InfoBox>
              )}
            </Panel>
          )}
        </div>
      )}

      {/* ══ ANOMALIES ══ */}
      {tab === 'anomaly' && (
        <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>
          <div style={{display:'flex',gap:'16px',alignItems:'center'}}>
            <div>
              <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'5px'}}>Z-SCORE THRESHOLD: {zThreshold}</div>
              <input type="range" min={1.5} max={5} step={0.5} value={zThreshold} onChange={e=>setZThreshold(parseFloat(e.target.value))} style={{width:'180px',accentColor:'var(--red)'}}/>
            </div>
            <Metric label="Anomalous Rows" value={anomalyResult.totalAnomalyRows} color={anomalyResult.totalAnomalyRows>0?'var(--red)':'var(--green)'} sub={`|z| > ${zThreshold} or IQR outlier`}/>
            <Metric label="Numeric Cols Checked" value={nc.length} color="var(--cyan)"/>
          </div>

          {nc.length === 0 && <InfoBox color="var(--gold)" icon="⚠">No numeric columns detected in active sheet.</InfoBox>}

          {anomalyResult.anomalies.length > 0 && (
            <Panel title="ANOMALOUS ROWS" badge={`${anomalyResult.totalAnomalyRows} rows flagged`} accent="var(--red)">
              <div style={{maxHeight:'450px',overflowY:'auto'}}>
                {anomalyResult.anomalies.slice(0,50).map((a,i)=>(
                  <div key={i} style={{padding:'10px 12px',background:i%2===0?'transparent':'rgba(0,0,0,.08)',borderBottom:'1px solid var(--b0)',display:'flex',gap:'12px',alignItems:'flex-start'}}>
                    <div style={{fontSize:'10px',color:'var(--t3)',minWidth:'50px'}}>row {a.rowIdx+1}</div>
                    <div style={{flex:1,display:'flex',flexWrap:'wrap',gap:'6px'}}>
                      {Object.entries(a.hits).map(([col,info])=>(
                        <div key={col} style={{padding:'4px 10px',background:'var(--red)15',border:'1px solid var(--red)44',borderRadius:'2px'}}>
                          <span style={{color:'var(--t1)',fontSize:'11px',fontWeight:600}}>{col}: </span>
                          <span style={{color:'var(--red)',fontSize:'11px'}}>{info.value}</span>
                          <span style={{color:'var(--t3)',fontSize:'9px',marginLeft:'5px'}}>|z|={info.z}{info.iqrFlag?' ⬟IQR':''}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          )}

          {/* Per-column anomaly summary */}
          {Object.keys(anomalyResult.colStats).length > 0 && (
            <Panel title="OUTLIER FENCES PER COLUMN" accent="var(--red)">
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                  <thead><tr style={{background:'var(--deep)'}}>
                    {['Column','Mean','Std','Q1','Q3','Lower Fence','Upper Fence','Outliers'].map(h=>(
                      <th key={h} style={{padding:'7px 12px',textAlign:'left',color:'var(--t3)',fontSize:'10px',borderBottom:'1px solid var(--b0)',whiteSpace:'nowrap'}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {Object.entries(anomalyResult.colStats).map(([col, cs],i)=>{
                      const col_anom = anomalyResult.anomalies.filter(a=>a.hits[col]).length;
                      return (
                        <tr key={col} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'transparent':'rgba(0,0,0,.08)'}}>
                          <td style={{padding:'7px 12px',color:'var(--t1)',fontWeight:500}}>{col}</td>
                          <td style={{padding:'7px 12px',color:'var(--t2)'}}>{cs.mean.toFixed(2)}</td>
                          <td style={{padding:'7px 12px',color:'var(--t2)'}}>{cs.std.toFixed(2)}</td>
                          <td style={{padding:'7px 12px',color:'var(--t2)'}}>{cs.q1.toFixed(2)}</td>
                          <td style={{padding:'7px 12px',color:'var(--t2)'}}>{cs.q3.toFixed(2)}</td>
                          <td style={{padding:'7px 12px',color:'var(--orange)'}}>{cs.lf.toFixed(2)}</td>
                          <td style={{padding:'7px 12px',color:'var(--orange)'}}>{cs.uf.toFixed(2)}</td>
                          <td style={{padding:'7px 12px',color:col_anom>0?'var(--red)':'var(--green)',fontWeight:600}}>{col_anom}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}
        </div>
      )}

      {/* ══ CORRELATION ══ */}
      {tab === 'correlate' && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 320px',gap:'14px'}}>
          <Panel title="CORRELATION MATRIX" badge={`${corrMatrix.cols.length} numeric cols`} accent="var(--green)">
            {corrMatrix.matrix.length > 0 ? (
              <div style={{overflowX:'auto'}}>
                <div style={{display:'inline-block',minWidth:'100%'}}>
                  <div style={{display:'flex',gap:'2px',marginLeft:'120px',marginBottom:'3px'}}>
                    {corrMatrix.cols.map(c=><div key={c} style={{width:'80px',fontSize:'9px',color:'var(--t3)',textAlign:'center',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',padding:'0 2px'}}>{c}</div>)}
                  </div>
                  {corrMatrix.matrix.map((row, i)=>(
                    <div key={i} style={{display:'flex',gap:'2px',alignItems:'center',marginBottom:'2px'}}>
                      <div style={{width:'118px',fontSize:'9px',color:'var(--t3)',textAlign:'right',paddingRight:'8px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{corrMatrix.cols[i]}</div>
                      {row.map((v, j)=>{
                        const abs = Math.abs(v);
                        return (
                          <div key={j} style={{width:'80px',height:'52px',borderRadius:'2px',background:v>=0?`rgba(0,217,255,${abs*.85})`:`rgba(255,53,86,${abs*.85})`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'11px',fontWeight:abs>.6?700:400,color:abs>.5?'var(--void)':'var(--t2)',cursor:'default'}} title={`r(${corrMatrix.cols[i]}, ${corrMatrix.cols[j]}) = ${v}`}>
                            {v.toFixed(2)}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  <div style={{display:'flex',gap:'16px',marginTop:'12px',fontSize:'10px'}}>
                    <span style={{color:'var(--cyan)'}}>■ positive</span>
                    <span style={{color:'var(--red)'}}>■ negative</span>
                    <span style={{color:'var(--t3)'}}>Intensity = |r|</span>
                  </div>
                </div>
              </div>
            ) : <InfoBox color="var(--gold)" icon="⚠">Need at least 2 numeric columns for correlation analysis.</InfoBox>}
          </Panel>

          <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
            <Panel title="STRONGEST CORRELATIONS">
              {(() => {
                const pairs = [];
                corrMatrix.cols.forEach((c1,i) => corrMatrix.cols.slice(i+1).forEach((c2,j)=>{
                  const v = corrMatrix.matrix[i]?.[i+j+1];
                  if (v !== undefined) pairs.push({c1,c2,v});
                }));
                pairs.sort((a,b)=>Math.abs(b.v)-Math.abs(a.v));
                return pairs.slice(0,10).map(({c1,c2,v})=>(
                  <div key={`${c1}${c2}`} style={{display:'flex',alignItems:'center',gap:'8px',padding:'7px 0',borderBottom:'1px solid var(--b0)'}}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:'10px',color:'var(--t1)',fontWeight:500}}>{c1}</div>
                      <div style={{fontSize:'9px',color:'var(--t3)'}}>↔ {c2}</div>
                    </div>
                    <div style={{fontSize:'13px',fontWeight:700,color:Math.abs(v)>.7?'var(--red)':Math.abs(v)>.4?'var(--gold)':'var(--t2)'}}>{v.toFixed(3)}</div>
                    <div style={{width:'40px',height:'6px',background:'var(--b0)',borderRadius:'3px'}}>
                      <div style={{height:'100%',width:`${Math.abs(v)*100}%`,background:v>=0?'var(--cyan)':'var(--red)',borderRadius:'3px'}}/>
                    </div>
                  </div>
                ));
              })()}
            </Panel>
          </div>
        </div>
      )}

      {/* ══ VISUALIZE ══ */}
      {tab === 'visualize' && (
        <div style={{display:'grid',gridTemplateColumns:'240px 1fr',gap:'14px'}}>
          <Panel title="CHART BUILDER">
            <div style={{marginBottom:'14px'}}>
              <div style={{fontSize:'10px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'7px'}}>CHART TYPE</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'4px'}}>
                {[['bar','Bar'],['line','Line'],['area','Area'],['scatter','Scatter'],['pie','Pie'],['histogram','Histogram'],['radar','Radar']].map(([t,l])=>(
                  <button key={t} onClick={()=>setVizType(t)} style={{padding:'8px',border:`1px solid ${vizType===t?'var(--green)':'var(--b0)'}`,background:vizType===t?'var(--ggreen)':'var(--raised)',color:vizType===t?'var(--green)':'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',transition:'all .12s'}}>{l}</button>
                ))}
              </div>
            </div>
            {['bar','line','area','scatter'].includes(vizType) && (
              <>
                <Sel value={vizX} onChange={setVizX} options={headers} label="X-AXIS"/>
                <div style={{height:'8px'}}/>
                <Sel value={vizY} onChange={setVizY} options={nc.length?nc:headers} label="Y-AXIS (numeric)"/>
              </>
            )}
            {['pie','histogram'].includes(vizType) && (
              <Sel value={statsCol} onChange={setStatsCol} options={headers} label="COLUMN"/>
            )}
            {vizType==='radar' && (
              <>
                <Sel value={vizX} onChange={setVizX} options={headers} label="GROUP BY"/>
                <div style={{height:'8px'}}/>
                <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'4px'}}>Uses top numeric columns</div>
              </>
            )}
          </Panel>

          <Panel title="CHART" badge={vizType.toUpperCase()} accent="var(--green)">
            {(() => {
              if (vizType === 'scatter') {
                const d = rows.slice(0,500).map(r=>({x:parseNum(r[vizX]),y:parseNum(r[vizY])})).filter(d=>!isNaN(d.x)&&!isNaN(d.y));
                return <ResponsiveContainer width="100%" height={420}><ScatterChart><CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/><XAxis dataKey="x" name={vizX} stroke="var(--t3)" tick={{fontSize:9}}/><YAxis dataKey="y" name={vizY} stroke="var(--t3)" tick={{fontSize:9}}/><Tooltip/><Scatter data={d} fill="var(--green)" opacity={.65}/></ScatterChart></ResponsiveContainer>;
              }
              if (vizType === 'pie') {
                const d = calcValueCounts(rows, statsCol, 9);
                return <ResponsiveContainer width="100%" height={420}><PieChart><Pie data={d} dataKey="count" nameKey="value" cx="50%" cy="50%" outerRadius={160} label={({value,percent})=>`${value} (${(percent*100).toFixed(0)}%)`} fontSize={10}>{d.map((_,i)=><Cell key={i} fill={P[i%P.length]}/>)}</Pie><Tooltip/></PieChart></ResponsiveContainer>;
              }
              if (vizType === 'histogram') {
                return <ResponsiveContainer width="100%" height={420}><BarChart data={calcHistogram(rows, statsCol, histBins)}><CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/><XAxis dataKey="range" stroke="var(--t3)" tick={{fontSize:8}} interval="preserveStartEnd"/><YAxis stroke="var(--t3)" tick={{fontSize:9}}/><Tooltip/><Bar dataKey="count" radius={[2,2,0,0]}>{calcHistogram(rows,statsCol,histBins).map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={.85}/>)}</Bar></BarChart></ResponsiveContainer>;
              }
              if (vizType === 'radar') {
                const groups = [...new Set(rows.map(r=>r[vizX]).filter(Boolean))].slice(0,8);
                const radarCols = nc.slice(0,6);
                const radarData = radarCols.map(col => {
                  const obj = {col};
                  groups.forEach(g => {
                    const gRows = rows.filter(r=>r[vizX]===g);
                    const vals = gRows.map(r=>parseNum(r[col])).filter(v=>!isNaN(v));
                    obj[g] = vals.length ? +(vals.reduce((s,v)=>s+v,0)/vals.length).toFixed(2) : 0;
                  });
                  return obj;
                });
                return <ResponsiveContainer width="100%" height={420}><RadarChart data={radarData}><PolarGrid stroke="var(--b1)"/><PolarAngleAxis dataKey="col" tick={{fill:'var(--t3)',fontSize:10}}/><PolarRadiusAxis tick={{fill:'var(--t3)',fontSize:8}}/>{groups.map((g,i)=><Radar key={g} dataKey={g} stroke={P[i%P.length]} fill={P[i%P.length]} fillOpacity={0.15}/>)}<Tooltip/><Legend/></RadarChart></ResponsiveContainer>;
              }
              const d = rows.slice(0,80).map(r=>({x:r[vizX],y:parseNum(r[vizY])||0}));
              if (vizType === 'line') return <ResponsiveContainer width="100%" height={420}><LineChart data={d}><CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/><XAxis dataKey="x" stroke="var(--t3)" tick={{fontSize:9}} interval="preserveStartEnd"/><YAxis stroke="var(--t3)" tick={{fontSize:9}}/><Tooltip/><Line type="monotone" dataKey="y" stroke="var(--green)" strokeWidth={2} dot={false}/></LineChart></ResponsiveContainer>;
              if (vizType === 'area') return <ResponsiveContainer width="100%" height={420}><AreaChart data={d}><CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/><XAxis dataKey="x" stroke="var(--t3)" tick={{fontSize:9}} interval="preserveStartEnd"/><YAxis stroke="var(--t3)" tick={{fontSize:9}}/><Tooltip/><Area type="monotone" dataKey="y" stroke="var(--green)" fill="var(--green)" fillOpacity={.15} strokeWidth={2}/></AreaChart></ResponsiveContainer>;
              return <ResponsiveContainer width="100%" height={420}><BarChart data={d}><CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/><XAxis dataKey="x" stroke="var(--t3)" tick={{fontSize:9}} interval="preserveStartEnd"/><YAxis stroke="var(--t3)" tick={{fontSize:9}}/><Tooltip/><Bar dataKey="y" radius={[2,2,0,0]}>{d.map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={.85}/>)}</Bar></BarChart></ResponsiveContainer>;
            })()}
          </Panel>
        </div>
      )}

      {/* ══ MISSING VALUE HEATMAP ══ */}
      {tab === 'missing' && (
        <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
            <Metric label="Total Nulls" value={nullTotal} color={nullTotal>0?'var(--gold)':'var(--green)'}/>
            <Metric label="Cols with Nulls" value={colTypes.filter(t=>(t.nullCount||0)>0).length} color="var(--orange)"/>
            <Metric label="Complete Rows" value={rows.filter(r=>headers.every(h=>r[h]!==''&&r[h]!=null)).length} color="var(--cyan)"/>
            <Metric label="Null%" value={`${(nullTotal/(rows.length*headers.length||1)*100).toFixed(1)}%`} color="var(--t2)" sub="of all cells"/>
          </div>

          <Panel title="MISSING VALUE HEATMAP" badge="first 80 rows × first 12 cols" accent="var(--gold)">
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'8px'}}>Red = missing cell. Each row is one data row. Each column is one field.</div>
            <div style={{overflowX:'auto'}}>
              <div style={{display:'flex',gap:'1px',marginLeft:'40px',marginBottom:'3px'}}>
                {headers.slice(0,12).map(h=><div key={h} style={{fontSize:'8px',color:'var(--t3)',width:'26px',textAlign:'center',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flexShrink:0}}>{h.slice(0,4)}</div>)}
              </div>
              {missingMatrix.map((row,i)=>(
                <div key={i} style={{display:'flex',gap:'1px',alignItems:'center',marginBottom:'1px'}}>
                  <div style={{fontSize:'8px',color:'var(--t3)',width:'38px',textAlign:'right',paddingRight:'3px',flexShrink:0}}>{i+1}</div>
                  {headers.slice(0,12).map(h=>(
                    <div key={h} style={{width:'26px',height:'8px',borderRadius:'1px',background:row[h]?'var(--red)':'var(--raised)',border:`1px solid ${row[h]?'var(--red)33':'var(--b0)'}`,flexShrink:0}} title={row[h]?`Row ${i+1}, ${h}: MISSING`:`Row ${i+1}, ${h}: present`}/>
                  ))}
                </div>
              ))}
            </div>
          </Panel>

          {/* Per-column null bar chart */}
          {colTypes.some(t=>(t.nullCount||0)>0) && (
            <Panel title="NULL RATE PER COLUMN" accent="var(--gold)">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={colTypes.filter(t=>(t.nullCount||0)>0).map(t=>({col:t.col,pct:+(t.nullCount/rows.length*100).toFixed(1)}))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" horizontal={false}/>
                  <XAxis dataKey="col" stroke="var(--t3)" tick={{fontSize:9}}/>
                  <YAxis stroke="var(--t3)" tick={{fontSize:9}} domain={[0,100]} label={{value:'%',angle:-90,position:'insideLeft',fill:'var(--t3)',fontSize:9}}/>
                  <Tooltip formatter={v=>[`${v}%`,'Null rate']}/>
                  <Bar dataKey="pct" radius={[2,2,0,0]}>
                    {colTypes.filter(t=>(t.nullCount||0)>0).map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={0.85}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          )}
        </div>
      )}
    </div>
  );
}